import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Search, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";
import { ImageField, Field } from "./ImageField";

const EMPTY = { name: "", category: "", image: "", tagline: "", description: "", specs: [] };

export const ProductsTab = () => {
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () =>
    api
      .get("/catalog/all")
      .then((r) => {
        setItems(r.data.items);
        setCategories(r.data.categories);
      })
      .catch((e) => toast.error(apiErr(e)));

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = cat === "All" ? items : items.filter((p) => p.category === cat);
    if (query.trim())
      list = list.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()));
    return list;
  }, [items, cat, query]);

  const save = async () => {
    if (!editing.name.trim() || !editing.category.trim()) {
      toast.error("Name and category are required");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/catalog/${editing.id}`, editing);
      else await api.post("/catalog", editing);
      toast.success(editing.id ? "Product updated" : "Product added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/catalog/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      setConfirmDelete(null);
      toast.success("Product deleted");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const togglePublish = async (p) => {
    const next = p.published === false;
    try {
      await api.patch(`/catalog/${p.id}/publish`, { published: next });
      setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, published: next } : x)));
      toast.success(next ? `"${p.name}" is now live` : `"${p.name}" moved to draft (hidden from website)`);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const setSpec = (i, key, val) => {
    const specs = editing.specs.map((s, idx) => (idx === i ? { ...s, [key]: val } : s));
    setEditing({ ...editing, specs });
  };

  if (!items) return <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-saffron" />;

  return (
    <div data-testid="admin-products">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-ink">Products & Services</h1>
          <p className="mt-1 font-body text-sm text-steel">
            {items.length} products in the catalogue
            {items.filter((p) => p.published === false).length > 0 &&
              ` · ${items.filter((p) => p.published === false).length} in draft (hidden from website)`}
          </p>
        </div>
        <button
          data-testid="product-add-btn"
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-2 rounded-full bg-saffron px-5 py-2.5 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft"
        >
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-steel/60" />
          <input
            data-testid="product-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products…"
            className="w-64 rounded-full border border-steel/25 bg-white py-2 pl-9 pr-4 font-body text-sm text-ink outline-none focus:border-saffron"
          />
        </div>
        <select
          data-testid="product-category-filter"
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="rounded-full border border-steel/25 bg-white px-4 py-2 font-body text-sm text-ink outline-none focus:border-saffron"
        >
          <option>All</option>
          {categories.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-steel/20">
        {filtered.map((p) => (
          <div
            key={p.id}
            data-testid={`product-row-${p.id}`}
            className="flex items-center gap-4 border-b border-steel/15 bg-aero-surface px-4 py-3 last:border-0"
          >
            <div className="grid h-12 w-16 shrink-0 place-items-center overflow-hidden rounded-md border border-steel/15 bg-white">
              {p.image && <img src={p.image} alt="" className="h-full w-full object-contain" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-sm font-semibold text-ink">
                {p.name}
                {p.published === false && (
                  <span data-testid={`product-draft-badge-${p.id}`} className="ml-2 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 font-heading text-[10px] font-semibold uppercase text-amber-700">
                    Draft
                  </span>
                )}
              </p>
              <p className="font-body text-xs text-steel">{p.category}</p>
            </div>
            <button
              data-testid={`product-publish-toggle-${p.id}`}
              onClick={() => togglePublish(p)}
              title={p.published === false ? "Publish — show on website" : "Move to draft — hide from website"}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-heading text-xs font-semibold transition-colors ${
                p.published === false
                  ? "border-green-300 text-green-700 hover:bg-green-50"
                  : "border-steel/25 text-steel hover:border-amber-400 hover:text-amber-600"
              }`}
            >
              {p.published === false ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {p.published === false ? "Publish" : "Draft"}
            </button>
            <button
              data-testid={`product-edit-${p.id}`}
              onClick={() => setEditing({ ...EMPTY, ...p })}
              className="inline-flex items-center gap-1.5 rounded-full border border-steel/25 px-3 py-1.5 font-heading text-xs font-semibold text-steel transition-colors hover:border-saffron hover:text-saffron"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            {confirmDelete === p.id ? (
              <button
                data-testid={`product-confirm-delete-${p.id}`}
                onClick={() => remove(p.id)}
                className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 font-heading text-xs font-semibold text-white"
              >
                <Trash2 className="h-3.5 w-3.5" /> Confirm?
              </button>
            ) : (
              <button
                data-testid={`product-delete-${p.id}`}
                onClick={() => setConfirmDelete(p.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 font-heading text-xs font-semibold text-red-500 transition-colors hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="bg-aero-surface p-8 text-center font-body text-sm text-steel">No products match.</p>
        )}
      </div>

      {/* Edit / Add dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="product-edit-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-steel/25 bg-aero-surface text-ink">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold text-ink">
                  {editing.id ? "Edit Product" : "Add Product"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Field label="Name" value={editing.name} testId="product-field-name" onChange={(v) => setEditing({ ...editing, name: v })} />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Category" value={editing.category} testId="product-field-category" onChange={(v) => setEditing({ ...editing, category: v })} />
                  <Field label="Tagline" value={editing.tagline} testId="product-field-tagline" onChange={(v) => setEditing({ ...editing, tagline: v })} />
                </div>
                <Field label="Description" textarea value={editing.description} testId="product-field-description" onChange={(v) => setEditing({ ...editing, description: v })} />
                <ImageField label="Product photo" value={editing.image} testId="product-image" onChange={(v) => setEditing({ ...editing, image: v })} />

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="font-heading text-xs font-semibold uppercase tracking-wide text-steel">Specifications</label>
                    <button
                      data-testid="product-add-spec-btn"
                      onClick={() => setEditing({ ...editing, specs: [...editing.specs, { label: "", value: "" }] })}
                      className="inline-flex items-center gap-1 font-heading text-xs font-semibold text-saffron"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add spec
                    </button>
                  </div>
                  <div className="space-y-2">
                    {editing.specs.map((s, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          value={s.label}
                          onChange={(e) => setSpec(i, "label", e.target.value)}
                          placeholder="Label"
                          className="w-1/3 rounded-lg border border-steel/25 bg-white px-3 py-2 font-body text-xs text-ink outline-none focus:border-saffron"
                        />
                        <input
                          value={s.value}
                          onChange={(e) => setSpec(i, "value", e.target.value)}
                          placeholder="Value"
                          className="flex-1 rounded-lg border border-steel/25 bg-white px-3 py-2 font-body text-xs text-ink outline-none focus:border-saffron"
                        />
                        <button
                          onClick={() => setEditing({ ...editing, specs: editing.specs.filter((_, idx) => idx !== i) })}
                          className="text-steel hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  data-testid="product-save-btn"
                  onClick={save}
                  disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-3 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editing.id ? "Save Changes" : "Add Product"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
